import { useState, FormEvent } from 'react'
import { apiFetch } from '@/lib/apiFetch'
import MagnumSays from '@/components/MagnumSays'

interface Props {
  onComplete: () => void
}

export default function SetProfileStep({ onComplete }: Props) {
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const passwordStrength = password.length === 0 ? 0
    : password.length < 8 ? 1
    : password.length < 12 ? 2
    : 3

  const strengthLabels = ['', 'Weak', 'Good', 'Strong']
  const strengthColors = ['', 'bg-red-400', 'bg-yellow-400', 'bg-green-400']

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError('')

    if (password.length < 10) {
      setError('Password must be at least 10 characters.')
      return
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.')
      return
    }

    setSubmitting(true)
    try {
      await apiFetch('/onboarding/profile', {
        method: 'PUT',
        body: JSON.stringify({ password, firstName, lastName }),
      })
      onComplete()
    } catch (err: any) {
      setError(err.message || 'Failed to save profile.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="w-full max-w-sm mx-auto space-y-6">
      <MagnumSays>
        <p className="font-medium">Welcome! I'm Tim.</p>
        <p className="mt-1 text-muted-foreground">Let's get you set up — just need your name and a password.</p>
      </MagnumSays>

      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="ob-firstName" className="block text-sm font-medium mb-1">First Name</label>
            <input
              id="ob-firstName"
              type="text"
              required
              autoFocus
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <div>
            <label htmlFor="ob-lastName" className="block text-sm font-medium mb-1">Last Name</label>
            <input
              id="ob-lastName"
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
        </div>

        <div>
          <label htmlFor="ob-password" className="block text-sm font-medium mb-1">Password</label>
          <input
            id="ob-password"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-3 py-2 border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="At least 10 characters"
          />
          {password.length > 0 && (
            <div className="mt-2 flex items-center gap-2">
              <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-300 ${strengthColors[passwordStrength]}`}
                  style={{ width: `${(passwordStrength / 3) * 100}%` }}
                />
              </div>
              <span className="text-xs text-muted-foreground">{strengthLabels[passwordStrength]}</span>
            </div>
          )}
        </div>

        <div>
          <label htmlFor="ob-confirm" className="block text-sm font-medium mb-1">Confirm Password</label>
          <input
            id="ob-confirm"
            type="password"
            required
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="w-full px-3 py-2 border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        <button
          type="submit"
          disabled={submitting}
          className="w-full py-2.5 bg-primary text-primary-foreground rounded-lg font-semibold hover:opacity-90 transition disabled:opacity-50"
        >
          {submitting ? 'Saving...' : 'Continue'}
        </button>
      </form>
    </div>
  )
}
